// Usage, billing, and workspace settings client.
//
// All three are tenant-admin-shaped concerns served by a handful of
// `/api/workspace/*` and `/api/usage/*` routes — grouped here.

import type { components } from "@/lib/api-types";

import {
  API,
  readErrorBody,
  requestJson,
  requestOk,
} from "../api";


type Schemas = components["schemas"];


// ---------------------------------------------------------------------------
// Workspace settings
// ---------------------------------------------------------------------------

export type WorkspaceSettings = Schemas["WorkspaceSettings"];
export type WorkspaceSettingsPatch = Schemas["WorkspaceSettingsPatch"];

export async function getWorkspaceSettings(signal?: AbortSignal): Promise<WorkspaceSettings> {
  return requestJson(`${API}/api/workspace/settings`, { signal });
}

export async function updateWorkspaceSettings(
  patch: WorkspaceSettingsPatch,
): Promise<WorkspaceSettings> {
  return requestJson(`${API}/api/workspace/settings`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}


// ---------------------------------------------------------------------------
// Feature registry
// ---------------------------------------------------------------------------

export type FeatureInfo = Schemas["FeatureInfo"];

export async function listFeatures(signal?: AbortSignal): Promise<FeatureInfo[]> {
  return requestJson(`${API}/api/features`, { signal });
}


// ---------------------------------------------------------------------------
// Usage + billing
// ---------------------------------------------------------------------------

export type UsagePlan = Schemas["UsagePlan"];
export type UsageByModel = Schemas["UsageByModel"];
export type UsageByThread = Schemas["UsageByTask"];
export type UsageSummary = Schemas["UsageSummary"];
export type PlanOption = Schemas["PlanOption"];

export async function getUsage(
  period: "month" | "all" = "month",
): Promise<UsageSummary> {
  return requestJson(`${API}/api/usage?period=${period}`);
}

// Platform-managed connector calls (Crustdata, Deliverables AI Data) — call
// and vendor-cost attribution, separate from the token/dollar ledger above.
// Data usage itself bills the regular credit wallet like any agent turn
// (ENG-3183), so there's no separate data-credit balance here.
export type ConnectorUsageSummary = Schemas["ConnectorUsageSummary"];

export async function getConnectorUsage(
  period: "month" | "all" = "month",
): Promise<ConnectorUsageSummary> {
  return requestJson(`${API}/api/usage/connectors?period=${period}`);
}

export async function listPlans(): Promise<Schemas["PlansResponse"]> {
  return requestJson(`${API}/api/usage/plans`);
}

export async function changePlan(plan_id: string): Promise<Schemas["PlanChangeResponse"]> {
  return requestJson(`${API}/api/usage/plan`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan_id }),
  });
}

// ---------------------------------------------------------------------------
// Stripe billing (live workspaces)
//
// For `billing_mode === "live"` workspaces the direct PATCH /api/usage/plan
// path 422s — a plan selection must go through Stripe instead. There are two
// cases: a workspace's FIRST subscription goes through Checkout; an existing
// subscription is updated in place via change-plan. The reliable signal for
// which is the backend itself — `startCheckout` returns 409 when an active
// subscription already exists (see CheckoutResponse docstring in the OpenAPI
// schema), so callers try checkout first and fall back to change-plan on 409.
//
// `/api/billing/checkout` returns 409 for TWO distinct reasons, so we read the
// `detail` body to discriminate: an active subscription already exists (caller
// should fall back to change-plan) vs. a checkout is already in progress
// (caller should NOT fall back — that would 409 again as "No active
// subscription to change"; show the user the in-progress message instead).
// ---------------------------------------------------------------------------

/** Sentinel thrown by `startCheckout` when the workspace already has an active
 * subscription (HTTP 409). Callers catch this to fall back to `changeBillingPlan`. */
export class HasActiveSubscriptionError extends Error {
  constructor(detail = "Workspace already has an active subscription") {
    super(detail);
    this.name = "HasActiveSubscriptionError";
  }
}

/** Sentinel thrown by `startCheckout` when a checkout is already in progress
 * for the workspace (HTTP 409). Distinct from `HasActiveSubscriptionError`:
 * callers must NOT fall back to change-plan (there is no subscription yet) —
 * they should surface the in-progress message and let the user finish or retry. */
export class CheckoutInProgressError extends Error {
  constructor(detail = "A checkout is already in progress for this workspace") {
    super(detail);
    this.name = "CheckoutInProgressError";
  }
}

/** Create a Stripe Checkout Session for a workspace's FIRST subscription.
 * Returns the URL to open in a new tab. On 409 throws either
 * `HasActiveSubscriptionError` (a subscription already exists — use change-plan)
 * or `CheckoutInProgressError` (a checkout is already in flight). */
export async function startCheckout(
  plan_id: string,
): Promise<Schemas["CheckoutResponse"]> {
  const r = await requestOk(
    `${API}/api/billing/checkout`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan_id }),
    },
    { allowStatus: [409] },
  );
  if (r.status === 409) {
    const { detail } = await readErrorBody(r);
    // The active-subscription conflict's detail mentions change-plan / "active
    // subscription"; everything else 409 here is the checkout-in-progress lock.
    if (/change-plan|active subscription/i.test(detail)) {
      throw new HasActiveSubscriptionError(detail);
    }
    throw new CheckoutInProgressError(detail);
  }
  return r.json() as Promise<Schemas["CheckoutResponse"]>;
}

export type ChangePlanPreview = Schemas["ChangePlanPreviewResponse"];

/** Preview the prorated cost of switching the existing subscription to
 * `plan_id`. Read-only — nothing is changed or charged; the UI shows the
 * result before calling `changeBillingPlan`. */
export async function previewPlanChange(
  plan_id: string,
): Promise<ChangePlanPreview> {
  return requestJson(`${API}/api/billing/change-plan/preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan_id }),
  });
}

/** Update an existing Stripe subscription in place (no second subscription,
 * no double-bill). Use for a live workspace that already has a subscription.
 * Pass the preview's `proration_date` so the billed proration matches what the
 * preview showed (the server 409s if it has gone stale — re-run the preview). */
export async function changeBillingPlan(
  plan_id: string,
  proration_date?: number,
): Promise<Schemas["ChangePlanResponse"]> {
  return requestJson(`${API}/api/billing/change-plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan_id, proration_date }),
  });
}

/** Create a Stripe Billing Portal session for managing the subscription. */
export async function openBillingPortal(): Promise<Schemas["PortalResponse"]> {
  return requestJson(`${API}/api/billing/portal`, { method: "POST" });
}

export type InvoiceSummary = Schemas["InvoiceSummary"];

/** List the workspace's Stripe invoices and legacy credit receipts (most recent
 * first) for billing history. Empty for a workspace with no Stripe customer. */
export async function listInvoices(): Promise<Schemas["InvoicesResponse"]> {
  return requestJson(`${API}/api/billing/invoices`);
}

/** Buy prepaid overage credits via hosted Stripe Checkout (mode=payment).
 * Returns a URL to open in a new tab; credits are granted on payment success. */
export async function buyCreditsCheckout(
  amount_usd: number,
  task_id?: string | null,
): Promise<Schemas["CreditsCheckoutResponse"]> {
  return requestJson(`${API}/api/billing/credits/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // `task_id` names the task the wall was quoted on, so the resume hook funds
    // THAT task before any other walled one (ENG-5279). Omitted when absent so
    // the field stays additive — the server defaults it to None.
    body: JSON.stringify(task_id ? { amount_usd, task_id } : { amount_usd }),
  });
}

/** Put a card on file for a Free-trial workspace via hosted Stripe Checkout
 * (mode=setup): no charge, no subscription. Unlocks the second tranche of the
 * signup grant once Stripe confirms the SetupIntent (ENG-5229). Returns a URL
 * to open; the unlock lands via webhook. */
export async function trialCardSetupCheckout(): Promise<
  Schemas["CreditsCheckoutResponse"]
> {
  return requestJson(`${API}/api/billing/trial/card-setup`, { method: "POST" });
}

/** Enable/disable + configure auto-reload of the prepaid credit balance. */
export async function setAutoReload(
  body: Schemas["AutoReloadSettings"],
): Promise<void> {
  await requestJson(`${API}/api/billing/auto-reload`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Set (or clear, with null) the monthly auto-reload spend cap. */
export async function setSpendLimit(
  monthly_spend_limit_usd: number | null,
): Promise<void> {
  await requestJson(`${API}/api/billing/spend-limit`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ monthly_spend_limit_usd }),
  });
}

export type PauseState = Schemas["PauseStateResponse"];

/** Pause the subscription — between-deals parking (ENG-2508). Billing stops
 * (Stripe pause_collection), reads stay open, unused monthly credits are
 * banked as non-expiring balance. Idempotent. */
export async function pauseSubscription(): Promise<PauseState> {
  return requestJson(`${API}/api/billing/pause`, { method: "POST" });
}

/** Resume a paused subscription — instant reactivation. A fresh billing
 * period is invoiced immediately; a declined card surfaces as a 402 whose
 * detail says to update the payment method first. */
export async function resumeSubscription(): Promise<PauseState> {
  return requestJson(`${API}/api/billing/resume`, { method: "POST" });
}
